import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Intention, ActionPlan, SummaryCardData } from '../types'
import { debouncedSaveJourney } from '../services/api'
import { useUserStore } from './useUserStore'

interface IntentionState {
  intentions: Intention[]
  actionPlans: ActionPlan[]
  summaryCard: SummaryCardData | null
  isGenerating: boolean

  // Actions
  setIntentions: (intentions: Intention[]) => void
  addIntention: (intention: Intention) => void
  updateIntention: (id: string, updates: Partial<Intention>) => void
  acceptIntention: (id: string) => void
  rejectIntention: (id: string) => void
  addActionPlan: (plan: ActionPlan) => void
  updateActionPlan: (intentionId: string, plan: Partial<ActionPlan>) => void
  setSummaryCard: (card: SummaryCardData) => void
  setGenerating: (value: boolean) => void
  getAcceptedIntentions: () => Intention[]
  reset: () => void
  syncToDatabase: () => void

  // Sync functions for D1
  loadFromServer: (data: {
    intentions?: Intention[]
    actionPlans?: ActionPlan[]
    summaryCard?: SummaryCardData | null
  }) => void
  getSnapshot: () => {
    intentions: Intention[]
    actionPlans: ActionPlan[]
    summaryCard: SummaryCardData | null
  }
}

const initialState = {
  intentions: [] as Intention[],
  actionPlans: [] as ActionPlan[],
  summaryCard: null,
  isGenerating: false,
}

export const useIntentionStore = create<IntentionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setIntentions: (intentions) => {
        set({ intentions })
        // Sync to database after setting intentions
        get().syncToDatabase()
      },

      addIntention: (intention) =>
        set((state) => ({
          intentions: [...state.intentions, intention],
        })),

      updateIntention: (id, updates) =>
        set((state) => ({
          intentions: state.intentions.map((i) =>
            i.id === id ? { ...i, ...updates } : i
          ),
        })),

      acceptIntention: (id) => {
        set((state) => ({
          intentions: state.intentions.map((i) =>
            i.id === id ? { ...i, accepted: true } : i
          ),
        }))
        // Sync to database after accepting
        get().syncToDatabase()
      },

      rejectIntention: (id) =>
        set((state) => ({
          intentions: state.intentions.filter((i) => i.id !== id),
        })),

      addActionPlan: (plan) => {
        set((state) => ({
          actionPlans: [...state.actionPlans, plan],
        }))
        // Sync to database after adding action plan
        get().syncToDatabase()
      },

      updateActionPlan: (intentionId, plan) =>
        set((state) => ({
          actionPlans: state.actionPlans.map((p) =>
            p.intentionId === intentionId ? { ...p, ...plan } : p
          ),
        })),

      setSummaryCard: (card) => set({ summaryCard: card }),

      setGenerating: (value) => set({ isGenerating: value }),

      getAcceptedIntentions: () => get().intentions.filter((i) => i.accepted),

      reset: () => set(initialState),

      // Sync current state to database
      syncToDatabase: () => {
        try {
          const { userId } = useUserStore.getState()
          const year = new Date().getFullYear()

          if (!userId) {
            console.warn('syncToDatabase skipped: no userId')
            return
          }

          const snapshot = get().getSnapshot()
          debouncedSaveJourney(userId, year, {
            intentions: snapshot.intentions,
            actionPlans: snapshot.actionPlans,
            summaryCard: snapshot.summaryCard,
          })
        } catch (error) {
          console.warn('Failed to sync intentions to database:', error)
        }
      },

      // Load data from server (used during initial sync)
      loadFromServer: (data) => {
        set({
          intentions: data.intentions ?? [],
          actionPlans: data.actionPlans ?? [],
          summaryCard: data.summaryCard ?? null,
        })
      },

      // Get current state for syncing to server
      getSnapshot: () => {
        const state = get()
        return {
          intentions: state.intentions,
          actionPlans: state.actionPlans,
          summaryCard: state.summaryCard,
        }
      },
    }),
    {
      name: `year-wrapped-intentions-${new Date().getFullYear()}`,
    }
  )
)
